import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const storedUser = localStorage.getItem('tpe_auth_user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setCurrentUser(userData);
          setIsAuthenticated(true);
        } catch (err) {
          console.error('Error parsing stored user data:', err);
          localStorage.removeItem('tpe_auth_user');
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = async (username, password) => {
    setError(null);
    try {
      // Using raw SQL query to handle login with our custom users table
      const { data, error } = await supabase.rpc('handle_login_attempt', {
        p_username: username,
        p_password: password
      });

      if (error) throw error;
      
      if (data && data.length > 0 && data[0].success) {
        const userData = {
          id: data[0].user_id,
          username,
          role: data[0].user_role
        };
        
        setCurrentUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('tpe_auth_user', JSON.stringify(userData));
        return { success: true };
      } else {
        return { 
          success: false, 
          error: data && data.length > 0 ? data[0].message : 'Login failed' 
        };
      }
    } catch (err) {
      console.error('Login error:', err);
      return { 
        success: false, 
        error: 'Authentication failed. Please try again later.' 
      };
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    localStorage.removeItem('tpe_auth_user');
  };

  const changePassword = async (currentPassword, newPassword) => {
    if (!currentUser) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const { data, error } = await supabase.rpc('change_password', {
        p_user_id: currentUser.id,
        p_current_password: currentPassword,
        p_new_password: newPassword
      });

      if (error) throw error;
      
      if (data && data.length > 0 && data[0].success) {
        return { success: true, message: data[0].message };
      } else {
        return { 
          success: false, 
          error: data && data.length > 0 ? data[0].message : 'Password change failed' 
        };
      }
    } catch (err) {
      console.error('Password change error:', err);
      return { 
        success: false, 
        error: 'Failed to change password. Please try again later.' 
      };
    }
  };

  const value = {
    isAuthenticated,
    currentUser,
    login,
    logout,
    loading,
    error,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};